import React from 'react';
import LegalPage, { Section, List, Link } from '@/components/Pages/LegalPage';
import Paragraph from '@/components/Typography/Paragraph';
import Text from '@/components/Typography/Text';
import MetaHead from '@/components/MetaHead';

const TermsOfServicePage = () => {
  return (
    <LegalPage
      title="Gab AI Service Terms of Service"
      lastUpdated="September 29, 2026"
    >
      <MetaHead 
        title="Terms of Service"
        description="Gab AI Terms of Service. User agreement, rights, responsibilities, and guidelines for using our AI platform, including Gab AI Business company workspaces. Must be 18+ to use."
        canonical="/terms-of-service"
        keywords="Gab AI terms, terms of service, user agreement, AI terms, legal terms"
      />

      <Paragraph>
        <Text weight='extraBold'>Welcome to Gab AI!</Text> These Terms of Service ("Terms") apply to your use of the Gab AI service and 
        GAB AI INC's other related services for individuals, businesses, and organizations, including Work and the Gab AI macOS app, along with any associated software
        applications and websites (collectively, the "Service"). These Terms form an agreement 
        between you and <Text weight='extraBold'>GAB AI INC</Text>, a Delaware corporation ("Gab AI," "we," 
        "our," or "us"). By using our Service, you agree to these Terms.
      </Paragraph>
      
      <Paragraph>
        Please note:
      </Paragraph>
      <List items={[
        'We reserve the right to modify these Terms.',
        'Please read our Privacy Policy which describes how we collect and use personal information.'
      ]} />

      <Section title="Who We Are">
        <Paragraph>
          Gab AI Inc is a US-based company located in Pennsylvania, working on building artificial
          intelligence tools. We are guided by our mission to make AI more approachable to enhance
          and augment human creativity. As part of our mission, we have developed "Arya" a conversational 
          generative AI powered by large language models. We have also integrated various third-party AI
          models from other companies.
        </Paragraph>
      </Section>

      <Section title="Registration and Access">
        <Paragraph>
          <Text weight='extraBold'>Minimum age.</Text> You must be at least <Text weight='extraBold'>18 years old</Text> to use the Service. By using this Service, you 
          represent and warrant that you are of legal age to form a binding contract with the Company 
          and meet this age requirement. If you do not meet this requirement, you must not access or use the Service.
        </Paragraph>
      </Section>
      <Section title="Registration">
        <Paragraph>
          You must provide accurate, current, and complete information to register 
          for an account to use our Service. You may not share your account credentials or make your 
          account available to anyone else and are responsible for all activities that occur under 
          your account. If you create an account or use the Service on behalf of another person or 
          entity, you must have the authority to accept these Terms on their behalf. Treat your 
          username, password, and any other security information as confidential. Notify us 
          immediately of any unauthorized access or use.
        </Paragraph>
      </Section>

      <Section title="Using our Service">
        <Paragraph>
          <Text weight='extraBold'>What you cannot do:</Text> You are prohibited from using our Service for any illegal or harmful 
          activity, or in any manner inconsistent with these Terms, including but not limited to:
        </Paragraph>
        
        <Paragraph>
          <Text weight='extraBold'>Detrimentally impacting the Service, including by:</Text>
        </Paragraph>
        <List items={[
          'Modifying, copying (except as permitted herein), leasing, selling, distributing, reverse engineering, or decompiling our Service.',
          'Using the Service or any Output to develop models or services that compete with Gab AI',
          'Disrupting, interfering with, damaging, overburdening, disabling, or gaining unauthorized access to the Service, its servers, or its safety systems (including via denial-of-service attacks).',
          'Using robots, spiders, or other automated means to access the Service for prohibited purposes like scraping or monitoring without authorization.',
          'Introducing viruses, Trojan horses, worms, logic bombs, or other malicious or technologically harmful material.'
        ]} />

        <Paragraph>
          <Text weight='extraBold'>Causing harm or engaging in abusive activity, including by:</Text>
        </Paragraph>
        <List items={[
          'Engaging in conduct that constitutes an unlawful threat or incites imminent lawless action not protected by the First Amendment.',
          'Exploiting, harming, or attempting to exploit or harm minors.',
          'Violating copyright, trademark, trade secret, patent, or other intellectual property law.',
          'Violating a person\'s legal rights (including privacy and publicity rights), or engaging in conduct that could give rise to civil or criminal liability under US law (e.g., unlawful doxing).',
          'Generating, sending, or promoting unwanted advertising or "spam."',
          'Impersonating others for a purpose not protected by the First Amendment.'
        ]} />

        <Paragraph>
          <Text weight='extraBold'>Not complying with laws or regulations, including by:</Text>
        </Paragraph>
        <List items={[
          'Using the Service in any way that violates applicable federal, state, or local US law and is not protected by the First Amendment.',
          'Generating or promoting content that is obscene, sexually explicit, or pornographic (mere nudity for educational/medical reasons may be permissible).',
          'Promoting illegal acts or advocating, aiding, or assisting any unlawful act.',
          'Engaging in prohibited commercial activities (certain financial/gambling promotions, illegal trade).',
          'Misleading others about the AI-generated nature of content where transparency is required or appropriate.'
        ]} />
      </Section>

      <Section title="Who the Service is Not Available To">
        <List items={[
          'Anyone who violates these Terms or our Content Standards.',
          'Anyone who has been previously suspended or terminated from the Service for violations.',
          'Anyone under the age of 18.'
        ]} />
      </Section>

      <Section title="Third-Party Services and Software">
        <Paragraph>
          Our Service may include links to or integrate with third-party software, products, or services subject 
          to their own terms.
        </Paragraph>
      </Section>

      <Section title="Content"></Section>
      <Section title="You own your Content">
        <Paragraph>
          You may provide input to the Service ("Input") and receive output from the 
          Service based on the Input ("Output"). Collectively, Input and Output stemming from your prompts 
          are "Content" or specifically "AI-Related User Contributions." You are responsible for your
          Content, including ensuring that it does not violate any applicable law or these Terms. You 
          represent and warrant that you have all rights, licenses, and permissions needed to provide Input 
          to our Service. As between you and Gab AI, and to the extent permitted by applicable law, you retain
          your rights in Input. Gab AI transfers to you whatever right, title, and interest it may hold in
          Output generated for you, including Work Output. This transfer does not cover the Service itself,
          another user's content, or material owned by a third party. Third-party code, software, media, and
          other materials may be subject to separate license terms. We do not promise that Output is unique,
          eligible for intellectual-property protection, or free of third-party rights. You are responsible
          for obtaining rights needed for your intended use.
        </Paragraph>
      </Section>
      <Section title="Our use of Content">
        <Paragraph>
          You grant Gab AI and its service providers a non-exclusive, worldwide, royalty-free license to
          process, store, reproduce, modify, and transmit your Content as needed to provide and maintain
          the Service, perform your requested tasks, provide support, and address security, legal, and
          compliance requirements, consistent with our Privacy Policy. Product and model improvement uses
          are subject to the disclosures and applicable choices described in our Privacy Policy and any
          consent required by law. This license does not authorize unrestricted publication or disclosure
          of your private Content for unrelated purposes. If you choose to share or publish Content, you
          authorize the distribution needed for that choice. Retention after deletion is subject to our
          Privacy Policy and applicable law.
        </Paragraph>

        <Paragraph>
          Automated systems may analyze your Content for safety and compliance. Limited authorized personnel 
          may review Content for specific business purposes like investigating incidents, and legal compliance.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Accuracy.</Text> Artificial intelligence is rapidly evolving and probabilistic. Output may contain
          inaccuracies, "hallucinations," offensive content, may not reflect real facts or people, or 
          may be otherwise unsuitable. Output does not constitute professional advice (legal, financial, medical, etc.).
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Similarity of content.</Text> Due to the nature of AI, Output may not be unique across users. Your
          rights to Output generated from your prompts do not grant rights over similar Output received by others.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Connecting to third-party services.</Text> If you connect to or send Content to third-party services 
          via features we provide, you authorize us to do so under that service's terms.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>When you use our Service, you understand and agree that:</Text>
        </Paragraph>
        <List items={[
          'Output may not always be accurate. Do not rely on Output as a sole source of truth or fact.',
          'You are responsible for evaluating Output for accuracy and appropriateness before use or sharing, including using human review.',
          'The Service may provide incomplete, incorrect, or offensive Output that does not represent Gab AI\'s views.',
          'We may implement rate limits at our discretion.'
        ]} />
      </Section>

      <Section title="Generated Statements and Company Commitments">
        <Paragraph>
          Output, including statements made by Gab AI bots, agents, or Work, does not by itself amend these Terms
          or another agreement, waive fees, approve refunds, guarantee results, or establish a policy or commitment
          on behalf of Gab AI. A generated statement that a refund, credit, purchase, or account change has been
          approved or completed is not by itself confirmation of that action. Verify consequential actions through
          the relevant account or billing records or an authorized Gab AI representative.
        </Paragraph>
        <Paragraph>
          This provision does not invalidate transactions actually completed through authorized Service controls,
          commitments expressly authorized by Gab AI, or rights and remedies provided by applicable law. It does
          not excuse Gab AI from legal obligations concerning representations made through the Service. Your
          authorization of Work Actions remains governed by the Work provisions below.
        </Paragraph>
      </Section>

      <Section title="Work and Automated Task Features">
        <Section title="1. Scope">
          <Paragraph>
            Gab AI Work and other automated task features (collectively, “Work”) are software tools that may research
            information, generate and edit documents, spreadsheets, presentations, software, images, audio, video,
            and other materials, execute code, interact with websites or connected services, and perform multi-step
            or scheduled tasks, depending on the features available to you. “Work Output” includes the materials and
            results generated through Work. “Work Actions” means operations performed through Work, including
            reading, creating, changing, transmitting, publishing, or deleting information and interacting with
            third-party accounts or services. Work is part of the Service, and Work Output is Output under these
            Terms.
          </Paragraph>
        </Section>
        <Section title="2. Your instructions and authorization">
          <Paragraph>
            You authorize Gab AI to process the information and use the tools and resources reasonably necessary to
            attempt the tasks you request, within the permissions, approvals, and limits applicable to those tasks.
            Work may select intermediate steps without requesting approval for each step. Access to an account or
            resource does not by itself authorize every action that is technically possible. Any separate
            confirmation required by the Service remains required. You represent that you have the authority and
            necessary rights and permissions to provide task materials, grant access, authorize actions, and act for
            any person or organization on whose behalf you use Work. Work does not give you rights to access or act
            on resources you are not authorized to use.
          </Paragraph>
        </Section>
        <Section title="3. Review and supervision">
          <Paragraph>
            You are responsible for the instructions, permissions, destinations, recipients, and spending approvals
            you provide and for reviewing Work Output before relying on, distributing, publishing, or deploying it.
            Review must be appropriate to the task and its potential consequences, including checking facts and
            sources, calculations and formulas, legal rights and licenses, and software functionality and security.
            Before approving an external action, review its material details, including the account, recipient,
            information being disclosed, and any price or commitment. You remain responsible for decisions you make
            using Work and for uses and actions you authorize, subject to rights and obligations that applicable law
            does not permit these Terms to exclude.
          </Paragraph>
        </Section>
        <Section title="4. Errors and task limitations">
          <Paragraph>
            Work may misunderstand instructions, choose unsuitable steps, use inaccurate or outdated information,
            produce incorrect calculations or insecure code, alter files incorrectly, or fail to complete a task.
            Websites, files, and other third-party materials may contain deceptive or malicious instructions intended
            to influence Work. Progress messages, previews, citations, tests, and statements that a task is complete
            are not certifications of accuracy, security, legal compliance, successful delivery, or suitability for
            your intended use. You should verify consequential results against the relevant files, systems, or third-party records. Keep independent copies of important information and test generated software before
            deployment.
          </Paragraph>
        </Section>
        <Section title="5. Connected accounts and third-party services">
          <Paragraph>
            When you direct Work to use an external account, website, application, or service, you authorize the
            access and transmission of information necessary for that task within your permissions. Information may
            include relevant task instructions, uploaded materials, page content, tool results, and identifying
            information contained in those materials. Independent third-party services have their own terms, fees,
            and data practices. You are responsible for complying with terms applicable to your authorized use and
            for transactions you authorize. To the extent permitted by law, Gab AI does not warrant or assume
            responsibility for an independent third party's products, information, performance, or handling of
            information it receives. This does not disclaim Gab AI's own obligations under applicable law or its
            Privacy Policy, including obligations relating to providers processing information on its behalf.
          </Paragraph>
        </Section>
        <Section title="6. Continuing tasks, schedules, and charges">
          <Paragraph>
            Where supported, a task or schedule you enable may continue while you are not viewing the Service. You
            are responsible for reviewing enabled schedules and permissions and using available controls to stop
            tasks or revoke access when they are no longer wanted. Stopping a task or disconnecting an account may
            not reverse an action already performed or immediately cancel a request already submitted to another
            service. Work may use multiple models, tools, and processing steps. Authorized usage is charged under the
            pricing and billing terms disclosed to you and may consume credits even if you stop the task or its
            result requires revision. Separate third-party purchases require the authorization applicable to that
            purchase. Enabling Work does not itself enable Auto Top-Up or authorize an unspecified purchase. Nothing
            in this paragraph overrides an express spending limit, required approval, refund commitment, or mandatory
            consumer right. Work usage may trigger purchases under Auto Top-Up if you have separately enabled it. An
            Auto Top-Up monthly limit controls automatic credit purchases, not task usage, manual purchases,
            subscription charges, or third-party charges. See our Terms of Sale for credit and Auto Top-Up terms.
          </Paragraph>
        </Section>
        <Section title="7. Professional and high-consequence uses">
          <Paragraph>
            Work does not provide licensed professional services or create an attorney-client, medical-provider,
            fiduciary, or similar professional relationship with Gab AI. You must obtain qualified professional
            advice where needed. You must not use Work as the sole basis for decisions that materially affect a
            person's legal rights, health, safety, employment, credit, housing, education, or insurance. Do not
            authorize Work to make autonomous investment transactions or control systems where an error could
            reasonably cause death, serious injury, or substantial physical damage. All use remains subject to
            applicable law and the other restrictions in these Terms.
          </Paragraph>
        </Section>
        <Section title="8. Work warranty disclaimer and allocation of liability">
          <Paragraph>
            TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, GAB AI DOES NOT GUARANTEE THAT WORK OUTPUT OR WORK
            ACTIONS WILL BE ACCURATE, COMPLETE, SECURE, NON-INFRINGING, SUITABLE FOR YOUR PURPOSE, OR SUCCESSFUL, OR
            THAT THEY WILL ACHIEVE A PARTICULAR BUSINESS RESULT. YOU ASSUME THE RISKS OF RELYING ON, USING, OR
            DISTRIBUTING WORK OUTPUT AND OF THE TASKS AND ACTIONS YOU AUTHORIZE. GAB AI'S DISCLAIMERS AND LIMITATIONS
            OF LIABILITY IN THESE TERMS EXPRESSLY APPLY TO WORK OUTPUT, WORK ACTIONS, AUTOMATED AND SCHEDULED TASKS,
            GENERATED OR MODIFIED FILES AND SOFTWARE, AND INTERACTIONS WITH THIRD-PARTY SERVICES. NOTHING IN THIS
            SECTION EXCLUDES OR LIMITS LIABILITY OR RIGHTS THAT CANNOT LAWFULLY BE EXCLUDED OR LIMITED.
          </Paragraph>
        </Section>
        <Section title="9. Availability and intervention">
          <Paragraph>
            Gab AI may pause, restrict, or discontinue a Work task or tool when reasonably necessary to address
            security incidents, suspected misuse, legal requirements, or service reliability. Work is not an
            emergency service, guaranteed monitoring service, or guaranteed backup or archival service. Any expressly
            agreed service commitments and mandatory legal rights remain applicable.
          </Paragraph>
        </Section>
      </Section>

      <Section title="macOS App, Local Files, and Computer Access">
        <Section title="1. Desktop capabilities">
          <Paragraph>
            The local file, command, browser, and computer-control capabilities of the Gab AI macOS app and
            associated tools (collectively, “Desktop Features”) can act on your computer and connected services.
            Depending on the features available and permissions you grant, they may read, search, create, edit, move, overwrite,
            or delete files; run code, tests, builds, commands, and development servers; access browser pages and
            signed-in accounts; read application content and capture screenshots of permitted windows; and click,
            type, submit information, or otherwise operate applications. These operations can affect your actual
            files, applications, accounts, and systems. For purposes of these Terms, Desktop Features are part
            of Work and the Service, their operations are Work Actions, and their results are Work Output.
          </Paragraph>
        </Section>
        <Section title="2. Permissions and authority">
          <Paragraph>
            By connecting a folder, selecting an application or browser, enabling a capability, or approving an
            access request, you authorize use of that access to attempt your requested tasks within the applicable
            permissions, instructions, approvals, and limits. Operating-system permissions, including Accessibility
            or Device Control and Data Access and Screen Recording permissions, may be needed for particular
            features. Folder access, command execution, development servers, browser or app access, and remote
            access have different scopes; review the access described when enabling each feature. Folder access
            does not itself authorize browser or application access, and any separately required permission must
            also be granted. Technical access does not authorize unrelated tasks or every action an account or
            application makes possible. Required confirmations remain required, but you should not expect a
            separate prompt for every intermediate step
            within an authorized task.
          </Paragraph>
          <Paragraph>
            You represent that you have authority to grant access to the devices, files, applications, accounts,
            and information involved, including any employer, client, or other third-party materials. You are
            responsible for obtaining necessary permissions and consents, providing required notices, and complying
            with applicable laws, organizational policies, confidentiality obligations, and third-party terms.
            Grant only the access needed for your tasks and review saved permissions before reusing them.
          </Paragraph>
        </Section>
        <Section title="3. Information accessed and transmitted">
          <Paragraph>
            Using a local app or folder does not mean all processing stays on your device. To carry out your tasks,
            relevant file contents and names, folder paths, application or browser content, screenshots, command
            inputs and results, and task context may be transmitted to and processed in the cloud by Gab AI and its
            hosting, model, and tool providers. Materials accessed for your tasks are Input under these Terms and are subject
            to the limited operational license under “Our use of Content” and our{' '}
            <Link href="/privacy-policy">Privacy Policy</Link>. This does not transfer ownership of your files to us
            or authorize unrelated access or public disclosure. Information sent to an independent website or
            recipient at your direction is also subject to the Work provisions on connected services.
          </Paragraph>
          <Paragraph>
            Files, logs, browser sessions, and visible application content may contain confidential information,
            personal information about others, or credentials even when you did not type that information into a
            prompt. Limit unnecessary exposure, keep secrets out of task materials where possible, and follow the
            “Regulated Data and Special Compliance Requirements” section when granting access to protected data.
          </Paragraph>
        </Section>
        <Section title="4. Review, supervision, and backups">
          <Paragraph>
            You are responsible for selecting appropriate access and supervising tasks in proportion to their
            consequences. Maintain independent, current backups of important files before allowing changes; use
            working copies or isolated test environments where appropriate; and review commands, generated code,
            file changes, recipients, uploads, and other consequential actions before approving or relying on them.
            Use only scripts, packages, tools, and integrations you are authorized to use and have reason to trust.
            Test changes before deploying them to production or applying them to important systems. Recovery
            history, previews, approval prompts, and other safeguards are not substitutes for your own backups
            and review. Gab AI does not undertake to supervise your computer or independently verify every action
            on your behalf.
          </Paragraph>
        </Section>
        <Section title="5. Computer-use risks">
          <Paragraph>
            Desktop Features may misunderstand a request, act on the wrong file or interface element, run faulty
            or harmful code, expose information, overwrite or delete data, change application state, or interrupt
            software or services. Changes to synchronized folders or signed-in services may propagate to other
            devices, shared workspaces, or collaborators. Commands and servers may use device resources, network
            connections, and credentials available within their permitted environment, and may incur third-party
            usage charges. Websites, messages, documents, repositories, and tool responses can contain malicious
            instructions intended to redirect the AI or extract information (“prompt injection”). Permission
            controls, isolation, warnings, and automated checks reduce risks but do not guarantee that all errors,
            harmful instructions, or unintended effects will be prevented. Some actions cannot be undone.
          </Paragraph>
        </Section>
        <Section title="6. Continuing activity and stopping access">
          <Paragraph>
            Where supported and enabled, tasks, remote requests, or local processes may continue while you are
            not viewing the app. Closing a conversation or completing or stopping a Work turn does not necessarily
            stop a separately running development server or scheduled task. Use the relevant task, server, schedule,
            folder, browser, app, and operating-system controls to stop activity or revoke access that is no longer
            wanted. Review and revoke other grants separately where the same resource is accessible through more
            than one connection. An operation already in progress or a request already submitted to another service
            may still complete. Revocation does not undo completed actions, sign you out of every third-party
            account, or delete materials already processed or retained under the Privacy Policy. Local operations
            may fail or be delayed when your device is asleep, offline, unavailable, or lacks the needed permissions.
          </Paragraph>
        </Section>
        <Section title="7. External accounts and commitments">
          <Paragraph>
            Actions performed through your signed-in applications and browser sessions may be attributed to you by
            those services. You remain responsible for lawful instructions and authorized communications, purchases,
            disclosures, and other commitments, and for checking their material details and results. An app or
            operating-system permission does not itself authorize an unspecified purchase, transfer, publication,
            or other unrelated commitment. The Work provisions on required approvals, spending limits, continuing
            tasks, and third-party services apply equally to Desktop Features.
          </Paragraph>
        </Section>
        <Section title="8. Desktop disclaimers and allocation of liability">
          <Paragraph>
            TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, DESKTOP FEATURES ARE PROVIDED “AS IS” AND “AS
            AVAILABLE.” YOU ASSUME THE RISKS OF THE ACCESS YOU GRANT AND THE TASKS AND ACTIONS YOU AUTHORIZE.
            GAB AI DOES NOT GUARANTEE THAT DESKTOP FEATURES WILL PRESERVE OR RECOVER FILES, PREVENT UNINTENDED
            CHANGES OR DISCLOSURES, OPERATE WITHOUT INTERRUPTION, OR BE COMPATIBLE WITH EVERY DEVICE, APPLICATION,
            OR SERVICE. THE WARRANTY DISCLAIMERS, BUSINESS INDEMNITY, AND SINGLE AGGREGATE LIABILITY LIMIT IN THESE
            TERMS APPLY TO DESKTOP FEATURES AND RELATED WORK ACTIONS, SUBJECT TO THEIR STATED CONDITIONS AND THE
            NON-WAIVABLE RIGHTS AND LIABILITIES PRESERVED IN THESE TERMS. THIS SECTION DOES NOT EXPAND YOUR
            AUTHORIZATION OR EXCLUDE ANY OBLIGATION OR LIABILITY THAT CANNOT LAWFULLY BE EXCLUDED.
          </Paragraph>
        </Section>
      </Section>

      <Section title="Acceptable Use of Bots">
        <Paragraph>
          In this section, “bots” and “agents” mean agentic AI tools provided by Gab AI, including Work and other
          features that perform tasks or take actions on your behalf. All uses of these tools, including scheduled
          tasks, must comply with these Terms and the restrictions under “Using our Service.”
          You must not use these features for the activities described below. These examples are not exhaustive;
          conduct prohibited elsewhere in these Terms remains prohibited when carried out through a bot. We
          may revise these examples as our features and patterns of misuse change, in accordance with the
          notification and changes provisions of these Terms.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Surveillance and unauthorized collection of personal information.</Text> You must not use bots to:
        </Paragraph>
        <List items={[
          'Track a person\'s online activity, behavior, or physical location without providing notice or obtaining consent.',
          'Gather, combine, or evaluate personal information to build profiles based on protected traits, sensitive characteristics, or personal circumstances.',
          'Identify people through facial recognition or other biometric identification tools or services.',
          'Carry out mass surveillance across websites or platforms to direct messages or other targeted actions at people.'
        ]} />

        <Paragraph>
          <Text weight='extraBold'>Harmful or deceptive content and impersonation.</Text> You must not use bots to:
        </Paragraph>
        <List items={[
          'Build or publish lookalike websites or domains that mislead visitors into believing they are dealing with a legitimate person or organization.',
          'Produce or distribute material that facilitates phishing, social engineering, scams, or other fraud.',
          'Pose as another person, whether a private individual or public figure, without that person\'s consent.'
        ]} />

        <Paragraph>
          <Text weight='extraBold'>Abuse at scale and coordinated manipulation.</Text> You must not use bots to:
        </Paragraph>
        <List items={[
          'Flood government services, emergency systems, or crisis support lines with spam or automated requests.',
          'Disrupt a website, server, or service by overwhelming it with traffic, including distributed denial-of-service attacks.',
          'Organize or carry out harassment through multiple accounts or across platforms.',
          'Artificially alter the results of online polls or voting systems, or falsify traffic and audience measurements.',
          'Set up or operate multiple accounts to avoid detection, bypass restrictions, or defeat platform safeguards.',
          'Run click farms or fabricate engagement, including automated likes, comments, or similar social media activity intended to inflate popularity.',
          'Automate coordinated influence campaigns that conceal their source or falsely present themselves as independent, authentic activity.',
          'Submit reports about people, accounts, or content in bulk through abuse-reporting systems.'
        ]} />

        <Paragraph>
          <Text weight='extraBold'>Unauthorized access, system interference, and transactions.</Text> You must not use bots to:
        </Paragraph>
        <List items={[
          'Place malware, backdoors, or monitoring software on a device or system without authorization.',
          'Run commands to exploit a system or obtain elevated access without authorization.',
          'Take actions that could compromise critical infrastructure or interfere with emergency services.',
          'Initiate unauthorized, unlawful, or fraudulent financial transactions or payment processing, including through brokerage or investment advisory services.',
          'Use saved passwords, tokens, or other credentials to access or change another person\'s account without permission.'
        ]} />
      </Section>

      <Section title="Gab AI's Intellectual Property Rights">
        <Paragraph>
          <Text weight='extraBold'>We own our Service.</Text> We and our affiliates own all rights, title, and interest in and to the Service
          itself (excluding your Content as defined above), including all software, text, images, designs, 
          and underlying technology. Gab AI® is a registered trademark of Gab AI, Inc. in the United States
          (U.S. Registration No. 8,471,213). The Company name, "GAB", logos, and related marks are trademarks of the Company.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Usage data relating to our Service.</Text> We may collect diagnostic, technical, usage, and related 
          information ("Usage Data"). All Usage Data is owned solely by us. You assign any rights you 
          may have in Usage Data to us. We may use Usage Data for any lawful purpose, including 
          providing, maintaining, and improving the Service, monitoring usage, research, analytics, and 
          sharing de-identified or aggregated data.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Feedback.</Text> If you provide suggestions, recommendations, or other feedback ("Feedback"), you 
          assign all rights, title, and interest in the Feedback to us. We are free to use Feedback 
          without attribution or compensation to you.
        </Paragraph>
      </Section>

      <Section title="Privacy and Data Security">
        <Paragraph>
          <Text weight='extraBold'>Privacy.</Text> By using the Service, you acknowledge our collection, use, and disclosure of personal 
          information as set forth in our <Link href="/privacy-policy">Privacy Policy</Link>. Your 
          information will be processed in the United States.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Security.</Text> We implement reasonable security measures but cannot guarantee absolute security. You 
          provide data at your own risk. Notify us immediately of any security breaches related to your account.
        </Paragraph>
      </Section>

      <Section title="Regulated Data and Special Compliance Requirements">
        <Paragraph>
          You must not submit data to the Service, or give it access to data, whose processing requires a special
          agreement, certification, authorization, or security environment that is not included in the Service
          provided to you, unless Gab AI has expressly agreed in writing to that specific use and all required
          agreements and safeguards are in place. This includes protected health information where a business
          associate agreement under the Health Insurance Portability and Accountability Act (“HIPAA”) is required,
          and other regulated data requiring processing arrangements beyond those provided under these Terms.
        </Paragraph>
        <Paragraph>
          You are responsible for determining whether your materials require those arrangements and for keeping
          your use within their agreed scope, including when you authorize a bot or connected tool to retrieve
          information. A subscription, enterprise inquiry, general description of our capabilities, or generated
          response does not itself establish a specialized compliance agreement. Any separately agreed enterprise
          or data-processing arrangement applies only to the services, data, and uses it covers. Nothing in this
          section excludes duties that applicable law imposes on Gab AI because of the processing it actually performs.
        </Paragraph>
      </Section>

      <Section title="Paid Accounts Fees; Payments; Cancellation">
        <Paragraph>
          These Terms apply to all subscription tiers, including Gab AI Plus, Gab AI Plus Premium, and Gab AI
          Business seats. The payment, renewal, cancellation, and refund provisions apply to each tier, together
          with our Terms of Sale and the applicable plan terms disclosed before purchase.
        </Paragraph>

        <Paragraph>
          If you purchase access to parts of the Service, you must provide 
          accurate billing information and a valid payment method. For subscriptions, we will automatically 
          charge your payment method periodically until cancellation. Taxes will be added where required. 
          Non-payment may result in suspension or downgrade. You can cancel subscriptions at any time; payments 
          are generally non-refundable except where required by law. Refer also to 
          our <Link href="/terms-of-sale">Terms of Sale</Link>.
        </Paragraph>

        <Paragraph>
          Credit packs and Quick Top-Up are one-time purchases. Auto Top-Up is optional and requires your
          separate authorization to charge your selected payment method when your available credits fall
          below your selected threshold. Work and other authorized usage may trigger enabled Auto Top-Up
          purchases, which can occur several times in one day during heavy use, within the monthly limit or
          daily purchase limit that applies. You can disable Auto Top-Up in your account settings; a payment already submitted may
          still complete. Pricing, spending limits, cancellation, and billing-error terms are set out in
          our <Link href="/terms-of-sale">Terms of Sale</Link>.
        </Paragraph>

        <Paragraph>
          Price changes. We may change subscription prices with at least 30 days' notice before your next renewal.
          Changes to an authorized Auto Top-Up pack require review and renewed authorization as described in
          the Terms of Sale.
        </Paragraph>
      </Section>

      <Section title="Gab AI Business (Company Workspaces)">
        <Section title="1. Scope and definitions">
          <Paragraph>
            Gab AI Business lets an organization create a company workspace (a “Business Workspace”), buy paid Plus
            and Plus Premium seats, share a pool of credits, and invite people to use company features. The
            “Organization” is the company or other entity on whose behalf a Business Workspace is created. An “Owner”
            is the person who creates the Business Workspace or is later designated as an owner. An “Admin” is a
            member given delegated management permissions, and a “Member” is any other person with an active seat.
            The Business Workspace is part of the Service. These Terms, the Work provisions above, and the{' '}
            <Link href="/terms-of-sale">Terms of Sale</Link> apply to it, and this section adds to them.
          </Paragraph>
        </Section>
        <Section title="2. Authority and responsibility">
          <Paragraph>
            If you create or manage a Business Workspace, you represent that you have authority to bind the
            Organization to these Terms and the Terms of Sale and to authorize its charges. The Organization is
            responsible for the activity of its Owners, Admins, and Members in the Business Workspace, for charges
            incurred there, and for making sure the people it invites are authorized and comply with these Terms. If
            you lack that authority, you are personally responsible for the obligations you accept. Each person
            using the Service remains bound by these Terms for their own use.
          </Paragraph>
        </Section>
        <Section title="3. Personal accounts stay separate">
          <Paragraph>
            Members use their own individual Gab AI accounts, and invitations are tied to the invited email address,
            which must be verified. Your personal chats, files, memory, subscriptions, and purchased credits stay in
            your personal account. They are not transferred to, shared with, or accessible to an Organization by
            creating or joining a Business Workspace. Content created in a Business Workspace stays associated with
            that workspace, and switching workspaces does not move it. Rights in that Content as between an
            Organization and its personnel are for them to agree; Gab AI does not decide those questions.
          </Paragraph>
        </Section>
        <Section title="4. Seats, roles, and administration">
          <Paragraph>
            Owners manage billing and paid capacity. Owners and Admins may, within their permissions, invite,
            revoke, suspend, and remove Members and manage their seat assignments and usage limits. A person’s role is separate
            from their Plus or Plus Premium seat. Pending invitations reserve a seat, are private to the invited
            email address, and expire if not accepted. We may rely on instructions given through these controls by a
            person acting as an Owner or Admin. A Business Workspace must keep at least one active Owner. Ownership
            transfer is not available in the Service yet; contact <Link href="mailto:ai@gab.com">ai@gab.com</Link> if
            an Owner must change. We are not responsible for disputes between an Organization and its personnel.
          </Paragraph>
        </Section>
        <Section title="5. Shared credits and limits">
          <Paragraph>
            Paid seats contribute credits to a company pool that is used for company work. Owners, and Admins within
            their permissions, can set a company monthly usage limit and Member limits. Limits include both completed usage and credits
            reserved for pending work, and a higher Member limit does not create company credits. When company
            credits or a limit are exhausted, new paid company work pauses. Company work does not charge a personal
            balance or card, and personal work does not use company credits. Included and purchased company
            credits are described in the Terms of Sale.
          </Paragraph>
        </Section>
        <Section title="6. What administrators can see">
          <Paragraph>
            Owners and Admins can see usage and activity information for the Business Workspace, including who used
            credits, when, on which feature or model, how many credits were used or remain pending, and a record of
            administrative actions. They can export usage reports. An Owner or Admin role does not by itself give
            access through the Service to another Member’s private prompts, chats, files, or outputs. Gab AI’s
            access to Content is governed by these Terms and our <Link href="/privacy-policy">Privacy Policy</Link>.
            The Organization is responsible for giving its personnel any notices and obtaining any consents required
            by law or its own policies for this visibility.
          </Paragraph>
        </Section>
        <Section title="7. Removal and offboarding">
          <Paragraph>
            Removing or suspending a Member ends their access to the Business Workspace and its Content and blocks
            new company work. Work already dispatched may still complete and settle charges against the company
            balance. Removal does not delete the person’s personal account. Records described in our Privacy Policy
            may be retained for accounting, security, dispute, and legal purposes. If an Organization needs data
            access or deletion, it should contact us before removing the people or Content concerned.
          </Paragraph>
        </Section>
        <Section title="8. Availability and scope">
          <Paragraph>
            Business Workspaces are created and billed on our website. Native apps do not sell or bill Business,
            though supported clients may open an existing company workspace. Features such as company Chat and Work,
            and the models available in them, depend on what is enabled for the workspace and may change. We do not
            currently offer single sign-on, company API keys, annual company plans, or automatic top-up for
            Business. Business does not include a separate service-level commitment, a data-processing agreement, or
            provider-specific data-use terms, and the “Regulated Data and Special Compliance Requirements” section
            applies. Any such commitment applies only if stated in a separate written agreement signed by an
            authorized Gab AI representative. We may suspend a Business Workspace, or new billable activity in it,
            for non-payment or for violations of these Terms.
          </Paragraph>
        </Section>
      </Section>

      <Section title="Termination, Suspension, Discontinuation">
        <Paragraph>
          <Text weight='extraBold'>Your choice to terminate.</Text> You may stop using the Service and close your account at any time.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Our choice to terminate.</Text> We may terminate or suspend your access or delete your account at 
          any time without notice if we determine, in our sole discretion, that:
        </Paragraph>
        <List items={[
          'You breached these Terms or our Content Standards.',
          'We must do so to comply with the law or court order.',
          'Your use poses a risk or potential harm to Gab AI, other users, or the public (including threats to physical safety).',
          'Your account has been inactive for an extended period.'
        ]} />

        <Paragraph>
          <Text weight='extraBold'>No refund.</Text> No refunds will be provided if termination is due to your violation of these Terms.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Appeals.</Text> If you believe suspension or termination was in error, contact us at support [at] gab [dot] ai.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Discontinuation of our Service.</Text> We reserve the right to discontinue the Service or parts thereof. 
          We will provide notice and potentially refunds for prepaid, unused services if applicable.
        </Paragraph>
      </Section>

      <Section title="Disclaimer of Warranties">
        <Paragraph>
          TO THE FULLEST EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. 
          YOUR USE OF THE SERVICE IS AT YOUR OWN RISK. THE SERVICE, ITS CONTENT (INCLUDING AI-GENERATED OUTPUT),
          AND ANY INFORMATION AVAILABLE THROUGH IT ARE PROVIDED WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR 
          IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR 
          PURPOSE, OR NON-INFRINGEMENT. NEITHER GAB AI INC NOR ANY PERSON ASSOCIATED WITH GAB AI INC MAKES ANY 
          WARRANTY OR REPRESENTATION REGARDING THE COMPLETENESS, SECURITY, RELIABILITY, QUALITY, ACCURACY, OR 
          AVAILABILITY OF THE SERVICE OR ITS OUTPUT. WE DO NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE OR 
          UNINTERRUPTED, THAT DEFECTS WILL BE CORRECTED, OR THAT THE SERVICE OR ITS SERVERS ARE FREE OF VIRUSES 
          OR OTHER HARMFUL COMPONENTS.
        </Paragraph>

        <Paragraph>
          YOU ACCEPT AND AGREE THAT ANY USE OF OUTPUTS FROM OUR SERVICE IS AT YOUR SOLE RISK. YOU WILL NOT 
          RELY ON OUTPUT AS A SOLE SOURCE OF TRUTH, FACTUAL INFORMATION, OR AS PROFESSIONAL ADVICE.
        </Paragraph>
      </Section>

      <Section title="Indemnity">
        <Paragraph>
          For purposes of these Terms, “Gab AI Indemnitees” means GAB AI INC, its affiliates, licensors, and
          service providers, and their respective officers, directors, employees, contractors, agents, suppliers,
          successors, and assigns.
        </Paragraph>
        <Paragraph>
          If you use the Service as a business or on behalf of an organization, you or that organization, as
          applicable, will defend and indemnify the Gab AI Indemnitees against third-party claims and associated
          damages, reasonable legal fees, and costs to the extent arising from: (a) materials, instructions,
          account access, or permissions you provide without the necessary authority or rights; (b) your use,
          publication, distribution, or deployment of Output (including Work Output) in violation of law or third-party rights; (c) Work Actions you authorize in violation of law, third-party rights, or applicable
          third-party terms, including actions through Desktop Features; or (d) your material breach of these
          Terms. This obligation does not apply to the extent a claim results from Gab AI's breach of these Terms,
          negligence, fraud, or willful misconduct. Gab
          AI will give prompt notice of the claim, provide reasonable cooperation at your expense, and permit you
          to control the defense using reasonably acceptable counsel. You may not settle a claim in a way that
          admits fault by, or imposes a nonmonetary obligation on, an indemnitee without its consent.
        </Paragraph>
        <Paragraph>
          These obligations apply only to the extent permitted by applicable law and are subject to the non-waivable rights and liabilities described below.
        </Paragraph>
      </Section>

      <Section title="Limitation of Liability">
        <Paragraph>
          TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, GAB AI INC AND THE GAB AI INDEMNITEES WILL NOT BE
          LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES ARISING OUT OF OR
          IN CONNECTION WITH THE SERVICE, INCLUDING LOSS OF PROFITS, REVENUE, BUSINESS OPPORTUNITIES, ANTICIPATED
          SAVINGS, GOODWILL, USE, OR DATA, EVEN IF ADVISED THAT SUCH DAMAGES WERE POSSIBLE.
        </Paragraph>
        <Paragraph>
          THESE LIMITATIONS APPLY TO THE SERVICE, OUTPUT (INCLUDING WORK OUTPUT), WORK ACTIONS, AUTOMATED AND
          SCHEDULED TASKS, DESKTOP FEATURES, LOCAL FILE AND COMPUTER ACCESS, CODE AND COMMAND EXECUTION,
          GENERATED OR MODIFIED FILES AND SOFTWARE, AND INTERACTIONS WITH THIRD-PARTY SERVICES, INCLUDING CLAIMS
          ARISING FROM ERRORS, OMISSIONS, FAILED OR DELAYED TASKS, DISCLOSURES, DELETION OR CORRUPTION OF DATA,
          UNINTENDED CHANGES, OR INTERRUPTION OF DEVICES, APPLICATIONS, OR SERVICES.
        </Paragraph>
        <Paragraph>
          TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, THE TOTAL AGGREGATE LIABILITY OF GAB AI INC AND THE
          GAB AI INDEMNITEES FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE WILL NOT
          EXCEED THE GREATER OF THE AMOUNT YOU PAID TO GAB AI UNDER THESE TERMS DURING THE SIX (6) MONTHS BEFORE
          THE EVENT GIVING RISE TO LIABILITY OR ONE HUNDRED U.S. DOLLARS ($100.00). THIS IS A SINGLE AGGREGATE
          LIMIT, NOT A SEPARATE LIMIT FOR EACH CLAIM, TASK, OR OUTPUT, AND APPLIES WHETHER LIABILITY IS BASED ON
          CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY, OR ANOTHER LEGAL THEORY.
        </Paragraph>
        <Paragraph>
          Nothing in these Terms, including the Work provisions, warranty disclaimers, indemnity, or liability
          limitations, excludes or limits liability for fraud, willful misconduct, gross negligence, death or
          personal injury, or any other liability to the extent applicable law prohibits its exclusion or
          limitation, or restricts any consumer right or remedy that cannot lawfully be waived. Some jurisdictions
          do not allow particular disclaimers or limitations, and you retain the protections required by the law
          applicable to you.
        </Paragraph>
      </Section>

      <Section title="Copyright Complaints">
        <Paragraph>
          If you believe your copyrighted work has been infringed via the Service, please notify our copyright 
          agent at legal@gab.ai. We may disable or remove content believed to be infringing and terminate accounts of 
          repeat infringers.
        </Paragraph>

        <Paragraph>
          Written claims must include:
        </Paragraph>
        <List items={[
          'Signature (electronic or physical) of the copyright owner or authorized person.',
          'Description of the copyrighted work.',
          'Description of the infringing material\'s location on the Service.',
          'Your contact information (address, phone, email).',
          'Statement of good-faith belief that the use is unauthorized.',
          'Statement under penalty of perjury that the information is accurate and you are authorized to act.'
        ]} />

        <Paragraph>
          Submit this information to: Attn: Copyright Agent, GAB AI INC, legal [at] gab [dot] ai
        </Paragraph>
      </Section>

      <Section title="Dispute Resolution">
        <Paragraph>
          READ THIS SECTION CAREFULLY. IT REQUIRES ARBITRATION AND LIMITS HOW YOU CAN SEEK RELIEF.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Mandatory arbitration.</Text> Except as provided below, you and GAB AI INC agree
          to resolve claims arising from these Terms or the Service (each, a “Claim”) through final and binding
          individual arbitration under this Arbitration Agreement. The Federal Arbitration Act governs the
          interpretation and enforcement of this Arbitration Agreement. Pennsylvania law governs the underlying
          Claim as provided in the Governing Law provision, subject to mandatory protections under applicable law.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Your right to opt out.</Text> You may opt out of arbitration by emailing
          legal [at] gab [dot] com with your full legal name and a clear statement that you wish to opt out, within
          30 days after you first accept this Arbitration Agreement. Users who accepted an earlier version also
          retain the right to opt out within 30 days after the September 14, 2026 revision takes effect for them.
          Any timely opt-out under an earlier version remains effective, and this revision does not shorten or
          eliminate an opt-out period or right already available to you. If you opt out, neither party is required
          to arbitrate with the other under this Arbitration Agreement. Other provisions of these Terms remain applicable.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Future arbitration changes.</Text> If we materially change this Arbitration
          Agreement, we will notify you of the changes and their effective date. You may opt out of arbitration
          within 30 days after the later of that notice or the effective date, using the method above. Changes
          solely to other parts of these Terms, or administrative corrections that do not materially change
          arbitration rights, do not create a new opt-out period. An arbitration amendment, including the
          September 14, 2026 revision, will not apply to a Claim about which either party gave the other written
          notice before that amendment took effect unless both parties agree otherwise after the dispute arises.
          The previously applicable agreement, if any, governs that Claim. Previously exercised opt-outs remain effective.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Informal resolution.</Text> Before starting arbitration, the party bringing a
          Claim must give written notice describing the dispute, their contact information, and the relief sought.
          Send your notice to legal [at] gab [dot] com; we will send ours to your account email address. The parties
          will attempt in good faith to resolve the Claim for 60 days after receipt. To the extent permitted by law,
          applicable filing deadlines for that Claim are paused during this period. This process does not prevent
          an eligible small-claims filing, an application for urgent relief, or a report to a government agency.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Administrator, rules, and hearings.</Text> Arbitration will be administered by
          the American Arbitration Association (“AAA”) under its applicable rules (“Rules”), including the Consumer
          Arbitration Rules for consumer disputes and the Commercial Arbitration Rules where applicable to business
          disputes. Mandatory law and the AAA's applicable consumer due-process requirements control any conflict
          with this Arbitration Agreement. A single neutral arbitrator will decide the Claim. Hearings may be held
          remotely, by documents where permitted, or at a location determined under the Rules with applicable
          consumer convenience protections. The Rules and fee schedules are available at{' '}
          <Link href="https://www.adr.org/rules-forms-and-fees/">AAA's rules and fees page</Link>.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Fees and administration.</Text> Fees, arbitrator compensation, and available fee
          waivers are governed by the applicable AAA fee schedule and law. Gab AI will pay the amounts assigned to
          the business in consumer cases and comply with applicable clause-review and registry requirements. Each
          party pays its own legal fees unless applicable law or the Rules authorize an award. If the AAA declines
          or ceases administration under its Rules, either party may proceed in an appropriate court unless the
          parties agree in writing to another administrator.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Coordinated filings.</Text> When the AAA determines that coordinated demands
          qualify for its mass-arbitration procedures, its applicable Mass Arbitration Supplementary Rules and fee
          schedule apply. The parties will follow the AAA's filing and case-management requirements. Administrative
          coordination does not authorize class arbitration or merge individual Claims for a decision on their
          merits without the parties' consent. This paragraph does not impose additional waiting periods or filing
          barriers beyond the applicable Rules and this Arbitration Agreement.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Court proceedings and authority.</Text> Either party may bring an eligible
          individual Claim in an appropriate small claims court, or seek injunctive or equitable relief in court
          to stop unauthorized use or abuse of the Service or infringement of intellectual property rights.
          A court decides whether an agreement to arbitrate was formed, whether an opt-out is effective, and the
          enforceability of the class-action waiver. Other questions of interpretation, scope, or enforceability
          are for the arbitrator except where applicable law requires a court to decide. Nothing prevents either
          party from reporting a concern to a government agency or seeking relief that cannot lawfully be restricted.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>CLASS ACTION AND JURY TRIAL WAIVERS.</Text> TO THE FULLEST EXTENT PERMITTED BY
          APPLICABLE LAW, CLAIMS SUBJECT TO ARBITRATION UNDER THIS AGREEMENT MUST BE RESOLVED ON AN INDIVIDUAL BASIS.
          YOU AND GAB AI WAIVE A JURY TRIAL FOR THOSE CLAIMS AND AGREE NOT TO PURSUE THEM IN ARBITRATION AS A CLASS,
          COLLECTIVE, OR REPRESENTATIVE ACTION. THE ARBITRATOR MAY AWARD RELIEF ONLY FOR THE INDIVIDUAL CLAIMANT,
          EXCEPT WHERE APPLICABLE LAW REQUIRES OTHERWISE. THESE RESTRICTIONS DO NOT WAIVE PUBLIC INJUNCTIVE RELIEF
          OR OTHER RIGHTS OR REMEDIES THAT CANNOT LAWFULLY BE WAIVED.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Severability.</Text> If a provision of this Arbitration Agreement is unenforceable,
          it will be severed to the extent permitted by law, and the remaining provisions will continue to apply.
          Any Claim or remedy that cannot lawfully be arbitrated may proceed in an appropriate court; remaining
          arbitrable Claims will proceed individually. No provision of these Terms authorizes class arbitration.
        </Paragraph>
      </Section>

      <Section title="General Provisions">
        <Paragraph>
          <Text weight='extraBold'>Assignment.</Text> You may not assign these Terms without our written consent. We may assign them without restriction.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Governing Law.</Text> These Terms are governed by the internal laws of the State
          of Pennsylvania, without regard to its conflict of laws principles, except that the Federal Arbitration
          Act governs the Arbitration Agreement. Subject to the small-claims option, mandatory law, and other court
          rights preserved in the Dispute Resolution section, permitted court actions must be brought in the
          federal or state courts located in <Text weight='extraBold'>Scranton, Lackawanna County, Pennsylvania.</Text>{' '}
          To the extent permitted by law, you and Gab AI consent to jurisdiction and venue there. This provision
          does not deprive consumers of protections or forums that applicable law does not permit them to waive.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Notification Procedures and Changes to these Terms.</Text> We may provide notifications via email or posting on the 
          Service. For material changes, we will provide notice describing the changes and when they take effect,
          with any advance notice or renewed acceptance required by applicable law. We will update the displayed
          'Last Updated' date. Continued use after the stated effective date constitutes acceptance only to the
          extent permitted by law; it does not replace any separately required consent, including authorization
          for Auto Top-Up charges or materially different uses of personal information. If you disagree, stop
          using the affected Service. The arbitration opt-out rights described above remain applicable.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Entire Agreement; Severability.</Text> These Terms, the{' '}
          <Link href="/privacy-policy">Privacy Policy</Link>, <Link href="/about/dmca">Copyright Policy</Link>,
          any applicable <Link href="/terms-of-sale">Terms of Sale</Link>, and any separate written agreement
          signed by you and an authorized Gab AI representative constitute the entire agreement regarding the
          Service within their respective scope. If a provision is invalid, the remaining provisions remain in
          effect, subject to the specific severability provisions in the Arbitration Agreement.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Order of precedence.</Text> If applicable agreements conflict, a separate written
          agreement signed by you and an authorized Gab AI representative controls for the services and subject
          matter it expressly covers. Subject to that agreement, the Terms of Sale control payment, credit,
          subscription, cancellation, and refund matters; the Privacy Policy controls our handling of personal
          information; and these Terms control other matters. A separate agreement changes dispute-resolution
          terms only if it expressly addresses them. This order does not override mandatory law, required consent,
          or privacy commitments that cannot lawfully be changed without notice or consent.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Survival.</Text> After your account is closed, your access ends, or these Terms
          terminate, provisions concerning accrued payment obligations, ownership of Content, Gab AI's intellectual
          property rights, applicable indemnity, warranty disclaimers, limitations of liability, and dispute
          resolution continue to apply to the extent relevant to rights or obligations arising from your use of
          the Service. A valid arbitration opt-out remains effective. Any continuing processing or retention of
          Content must remain within the purposes and limits stated in these Terms and the Privacy Policy. This
          provision does not authorize new charges or continued account access, or eliminate applicable deletion,
          cancellation, refund, or other mandatory rights.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>No Waiver.</Text> Our failure to assert any right does not constitute a waiver.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>California Residents.</Text> Per Cal. Civ. Code §1789.3, complaints can be reported to the Complaint Assistance 
          Unit of the Division of Consumer Services of the California Department of Consumer Affairs.
        </Paragraph>

        <Paragraph>
          <Text weight='extraBold'>Contact.</Text> Questions? Contact us at support [at] gab [dot] ai
        </Paragraph>
      </Section>

      <Section title="Export Controls">
        <Paragraph>
          You agree to comply with all applicable U.S. export control and economic sanctions laws and regulations. 
          You represent you are not on any U.S. government list of prohibited/restricted parties and are not 
          located in/national of a U.S. embargoed country or territory designated as supporting terrorism. You will 
          not make the Service available in violation of these laws.
        </Paragraph>
      </Section>
    </LegalPage>
  );
};

export default React.memo(TermsOfServicePage);
